export type BlogStatus = 'draft' | 'published'

export type BlogArticle = {
  id: string
  slug: string
  title: string
  summary: string
  content: string
  category: string
  tags: string[]
  cover_image_url?: string | null
  status: BlogStatus
  published_at?: string | null
  view_count?: number | null
}

export type BlogFormInput = {
  slug?: string | null
  title?: string | null
  summary?: string | null
  content?: string | null
  category?: string | null
  tags?: string[] | string | null
  cover_image_url?: string | null
  status?: BlogStatus | string | null
}

function normalizeText(value: string | undefined | null) {
  return value?.trim() ?? ''
}

export function slugifyKorean(value: string) {
  return normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9가-힣\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 90)
}

export function normalizeBlogTags(tags: string[] | string | null | undefined) {
  const list = Array.isArray(tags) ? tags : normalizeText(tags).split(',')
  return [...new Set(list.map((tag) => normalizeText(tag).replace(/^#/, '')).filter(Boolean))].slice(0, 12)
}


const SEO_TAG_STOPWORDS = new Set(['그리고', '대한', '위한', '관련', '알아보기', '가이드', '완벽', '정리'])

export function deriveBlogSeoTags(tags: string[] | string | null | undefined, title: string | null | undefined, category: string | null | undefined) {
  const normalized = normalizeBlogTags(tags)
  const candidates = [
    normalizeText(category),
    ...normalizeText(title).split(/[\s,·:|/()[\]{}]+/),
  ]
  for (const candidate of candidates) {
    const tag = normalizeText(candidate).replace(/^#/, '')
    if (tag.length < 2 || SEO_TAG_STOPWORDS.has(tag) || normalized.includes(tag)) continue
    normalized.push(tag)
    if (normalized.length >= 8) break
  }
  return normalized
}

export type BlogSeoCheck = {
  id: 'title' | 'description' | 'slug' | 'tags' | 'content' | 'headings' | 'image'
  label: string
  passed: boolean
  points: number
  recommendation: string
}

export function getBlogSeoAudit(input: BlogFormInput) {
  const title = normalizeText(input.title)
  const content = normalizeText(input.content)
  const description = makeSeoSummary(input.summary, content)
  const slug = slugifyKorean(normalizeText(input.slug) || title)
  const tags = deriveBlogSeoTags(input.tags, title, input.category)
  const plainTextLength = getBlogPlainText(content).length
  const checks: BlogSeoCheck[] = [
    { id: 'title', label: '검색 제목', passed: title.length >= 10 && title.length <= 60, points: 15, recommendation: '제목을 10~60자로 작성하세요.' },
    { id: 'description', label: '검색 설명', passed: description.length >= 50 && description.length <= 160, points: 20, recommendation: '핵심 키워드를 포함한 설명을 50~160자로 작성하세요.' },
    { id: 'slug', label: 'SEO 주소', passed: Boolean(slug) && slug.length <= 90, points: 10, recommendation: '간결하고 읽기 쉬운 SEO 주소를 사용하세요.' },
    { id: 'tags', label: '핵심 태그', passed: tags.length >= 3 && tags.length <= 8, points: 15, recommendation: '관련 태그를 3~8개 사용하세요.' },
    { id: 'content', label: '본문 충실도', passed: plainTextLength >= 500, points: 20, recommendation: '검색자가 충분히 이해할 수 있도록 본문을 500자 이상 작성하세요.' },
    { id: 'headings', label: '소제목 구조', passed: /^##\s+.+/m.test(content), points: 10, recommendation: '본문에 H2 소제목(##)을 한 개 이상 추가하세요.' },
    { id: 'image', label: '대표 이미지', passed: Boolean(normalizeText(input.cover_image_url)), points: 10, recommendation: '공유 카드와 검색 이미지용 대표 이미지를 등록하세요.' },
  ]
  return {
    score: checks.reduce((score, check) => score + (check.passed ? check.points : 0), 0),
    checks,
    description,
    slug,
    tags,
  }
}

export function getBlogPlainText(markdown: string) {
  return normalizeText(markdown)
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*\]\((?:https?:\/\/[^\s)]+|\/[^\s)]+)\)/g, ' ')
    .replace(/\[([^\]]+)\]\((?:https?:\/\/[^\s)]+|\/[^\s)]+|#[^\s)]+)\)/g, '$1')
    .replace(/[#>*_`~|:-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function makeSeoSummary(summary: string | null | undefined, content: string, maxLength = 155) {
  const base = normalizeText(summary) || getBlogPlainText(content)
  return base.length > maxLength ? `${base.slice(0, maxLength - 1).trim()}…` : base
}

export function estimateReadingMinutes(markdown: string) {
  const chars = getBlogPlainText(markdown).length
  return Math.max(1, Math.ceil(chars / 500))
}

export function validateBlogForm(input: BlogFormInput) {
  const errors: string[] = []
  if (!normalizeText(input.title)) errors.push('제목을 입력해주세요.')
  if (!normalizeText(input.content)) errors.push('마크다운 본문을 입력해주세요.')
  const slug = slugifyKorean(normalizeText(input.slug) || normalizeText(input.title))
  if (!slug) errors.push('SEO 주소(slug)를 입력하거나 제목을 보완해주세요.')
  if (makeSeoSummary(input.summary, normalizeText(input.content)).length < 40) errors.push('SEO 설명은 40자 이상 권장합니다.')
  return { ok: errors.length === 0, errors, slug }
}

export function buildBlogInsertPayload(input: BlogFormInput) {
  const content = normalizeText(input.content)
  const status = input.status === 'draft' ? 'draft' as const : 'published' as const
  const slug = slugifyKorean(normalizeText(input.slug) || normalizeText(input.title))
  return {
    slug,
    title: normalizeText(input.title),
    summary: makeSeoSummary(input.summary, content),
    content,
    category: normalizeText(input.category) || '음악치료 이해',
    tags: deriveBlogSeoTags(input.tags, input.title, input.category),
    cover_image_url: normalizeText(input.cover_image_url) || null,
    status,
    published_at: status === 'published' ? new Date().toISOString() : null,
  }
}

export function buildBlogUpdatePayload(input: BlogFormInput, existingPublishedAt?: string | null) {
  const payload = buildBlogInsertPayload(input)
  return {
    ...payload,
    published_at: payload.status === 'published' ? (existingPublishedAt || new Date().toISOString()) : null,
  }
}



const BLOG_SITE_URL = 'https://mcarelab.co.kr'

export function buildBlogStructuredData(article: BlogArticle) {
  const url = `${BLOG_SITE_URL}/blog/${article.slug}`
  const image = normalizeText(article.cover_image_url)
  return {
    blogPosting: {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.summary,
      url,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      ...(image ? { image } : {}),
      datePublished: article.published_at ?? undefined,
      author: { '@type': 'Organization', name: '뮤직캐어랩', url: BLOG_SITE_URL },
      publisher: { '@type': 'Organization', name: '뮤직캐어랩', url: BLOG_SITE_URL },
      articleSection: article.category,
      keywords: article.tags.join(', '),
      inLanguage: 'ko-KR',
      isAccessibleForFree: true,
      wordCount: getBlogPlainText(article.content).split(/\s+/).filter(Boolean).length,
    },
    breadcrumb: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '홈', item: BLOG_SITE_URL },
        { '@type': 'ListItem', position: 2, name: '블로그', item: `${BLOG_SITE_URL}/blog` },
        { '@type': 'ListItem', position: 3, name: article.title, item: url },
      ],
    },
  }
}

export type BlogContentBlock =
  | { type: 'heading'; level: 1 | 2 | 3; value: string; id: string }
  | { type: 'paragraph'; value: string }
  | { type: 'list'; ordered: boolean; items: string[] }
  | { type: 'quote'; value: string }
  | { type: 'image'; alt: string; url: string }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'code'; value: string; language?: string }

export type BlogTocItem = { id: string; title: string; level: 2 | 3 }

function splitTableRow(line: string) {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim())
}

function isTableSeparator(line: string) {
  return /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line.trim())
}

function makeUniqueId(value: string, used: Map<string, number>) {
  const base = slugifyKorean(value) || 'section'
  const count = used.get(base) ?? 0
  used.set(base, count + 1)
  return count ? `${base}-${count + 1}` : base
}

export function getBlogToc(markdown: string): BlogTocItem[] {
  const used = new Map<string, number>()
  return markdown.split(/\r?\n/).flatMap((line) => {
    const match = line.trim().match(/^(##|###)\s+(.+)/)
    if (!match) return []
    const title = match[2].replace(/\s*\{#.+\}\s*$/, '').trim()
    if (title === '목차' || title === 'SEO 설정') return []
    return [{ id: makeUniqueId(title, used), title, level: match[1].length as 2 | 3 }]
  })
}

export function parseBlogMarkdown(markdown: string): BlogContentBlock[] {
  const blocks: BlogContentBlock[] = []
  const lines = markdown.split(/\r?\n/)
  const usedHeadingIds = new Map<string, number>()
  let paragraph: string[] = []
  let list: string[] = []
  let listOrdered = false
  let quote: string[] = []
  let code: string[] = []
  let codeLanguage = ''
  let inCode = false
  function flushParagraph() {
    if (paragraph.length) {
      blocks.push({ type: 'paragraph', value: paragraph.join('\n').trim() })
      paragraph = []
    }
  }
  function flushList() {
    if (list.length) {
      blocks.push({ type: 'list', ordered: listOrdered, items: list })
      list = []
      listOrdered = false
    }
  }
  function flushQuote() {
    if (quote.length) {
      blocks.push({ type: 'quote', value: quote.join('\n').trim() })
      quote = []
    }
  }
  for (let index = 0; index < lines.length; index += 1) {
    const raw = lines[index]
    const line = raw.trim()
    const fence = line.match(/^```\s*([a-zA-Z0-9_-]+)?\s*$/)
    if (fence) {
      if (inCode) {
        blocks.push({ type: 'code', value: code.join('\n'), language: codeLanguage || undefined })
        code = []
        codeLanguage = ''
        inCode = false
      } else {
        flushParagraph(); flushList(); flushQuote()
        inCode = true
        codeLanguage = fence[1] ?? ''
      }
      continue
    }
    if (inCode) { code.push(raw); continue }
    if (!line) { flushParagraph(); flushList(); flushQuote(); continue }
    if (line.includes('|') && index + 1 < lines.length && isTableSeparator(lines[index + 1])) {
      flushParagraph(); flushList(); flushQuote()
      const headers = splitTableRow(line)
      index += 2
      const rows: string[][] = []
      while (index < lines.length && lines[index].trim().includes('|')) {
        rows.push(splitTableRow(lines[index]))
        index += 1
      }
      index -= 1
      blocks.push({ type: 'table', headers, rows })
      continue
    }
    const image = line.match(/^!\[([^\]]*)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)$/)
    if (image) { flushParagraph(); flushList(); flushQuote(); blocks.push({ type: 'image', alt: image[1] || '블로그 이미지', url: image[2] }); continue }
    const heading = line.match(/^(#{1,3})\s+(.+)/)
    if (heading) {
      flushParagraph(); flushList(); flushQuote()
      const value = heading[2].replace(/\s*\{#.+\}\s*$/, '').trim()
      blocks.push({ type: 'heading', level: heading[1].length as 1 | 2 | 3, value, id: makeUniqueId(value, usedHeadingIds) })
      continue
    }
    const bullet = line.match(/^[-*]\s+(.+)/)
    const ordered = line.match(/^\d+[.)]\s+(.+)/)
    if (bullet || ordered) {
      flushParagraph(); flushQuote()
      const isOrdered = Boolean(ordered)
      if (list.length && listOrdered !== isOrdered) flushList()
      listOrdered = isOrdered
      list.push((ordered?.[1] ?? bullet?.[1] ?? '').trim())
      continue
    }
    const quoteLine = line.match(/^>\s?(.+)/)
    if (quoteLine) { flushParagraph(); flushList(); quote.push(quoteLine[1]); continue }
    flushList(); flushQuote()
    paragraph.push(raw)
  }
  if (inCode) blocks.push({ type: 'code', value: code.join('\n'), language: codeLanguage || undefined })
  flushParagraph(); flushList(); flushQuote()
  return blocks.length ? blocks : [{ type: 'paragraph', value: markdown }]
}

export function renderInlineMarkdown(value: string) {
  return value
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
}

export function formatBlogDate(value: string | null | undefined) {
  if (!value) return '발행 전'
  return new Intl.DateTimeFormat('ko-KR', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(value))
}
